// The datasets in the publish dialog: those a document's UI reads, shown
// before publishing ("Datasets published with it") and while published (with
// "Publish datasets" for those read but not published yet), and the preview's
// data.
//
// - Nothing is added when the document reads no dataset.
// - Each one: its title (the dialog turns to the dataset, `onView`; from there
//   "Open dataset" goes to its screen), its space, its rows and its
//   fields (five, then how many more). One that cannot be published says why.
//   One the person does not see is only counted (no name).
// - The preview frame has no origin and asks this dialog for its UIs' data
//   (`PUB_PREVIEW_SOURCE`). Only messages from that frame's window count, and
//   only the datasets shown as going out are answered; any other is
//   `not_found`. The answer is the query's result (the screen's own copy of the
//   dataset runs it), never the rows.
import { useQuery } from "@tanstack/react-query";
import { meDatasetPath } from "@trameria/core/datasets-shared";
import {
  PUB_PREVIEW_SOURCE,
  type PublicationDatasetDTO,
  type PublicationDatasetsDTO,
} from "@trameria/core/publishing-shared";
import { m } from "@trameria/i18n";
import { type RefObject, useEffect, useState } from "react";
import { useApi } from "../app/context";
import { apiErrorMessage } from "../data/errors";
import { type DatasetInPlace, DatasetViewerLink } from "../datasets/viewer";
import { ErrorLine } from "../ui/bits";
import { Button } from "../ui/button";
import { confirmAction } from "../ui/confirm";
import {
  readDataWants,
  type UiDataScope,
  useFenceData,
} from "../ui-fence/data";
import { publicationDatasetsQuery, useAddDatasets } from "./queries";

const NOTE = "text-[12.5px] leading-[1.7] text-muted";
const FIELDS_SHOWN = 5;

const reasonOf = (d: PublicationDatasetDTO): string | null =>
  d.state === "not_writable"
    ? m.publishing_dataset_not_writable()
    : d.state === "not_allowed"
      ? m.publishing_dataset_not_allowed()
      : null;

function fieldsLine(fields: readonly string[]): string {
  const shown = fields.slice(0, FIELDS_SHOWN).join(", ");
  const more = fields.length - FIELDS_SHOWN;
  return more > 0
    ? `${shown} ${m.publishing_dataset_more_fields({ count: more })}`
    : shown;
}

function DatasetRows({
  items,
  onView,
}: {
  items: readonly PublicationDatasetDTO[];
  onView(dataset: DatasetInPlace): void;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-col divide-y divide-border-soft rounded-[9px] border border-border">
      {items.map((d) => {
        const reason = reasonOf(d);
        const meta = [
          d.space,
          m.publishing_dataset_rows({ count: d.rows }),
          d.fields.length ? fieldsLine(d.fields) : null,
        ].filter(Boolean);
        const dataset: DatasetInPlace = {
          id: d.id,
          title: d.title,
          to: d.workspace
            ? `/${d.workspace}/documents/datasets/${d.id}`
            : meDatasetPath(d.id),
          place: d.workspace ?? undefined,
        };
        return (
          <li key={d.id} className="flex min-w-0 flex-col gap-0.5 px-3 py-2">
            {/* The dialog turns to it (and back). */}
            <DatasetViewerLink
              dataset={dataset}
              onView={() => onView(dataset)}
              className="truncate text-[13px] text-fg hover:underline"
            >
              {d.title}
            </DatasetViewerLink>
            <span className="truncate text-[12px] text-muted">
              {meta.join(" · ")}
            </span>
            {reason && (
              <span className="text-[12px] leading-[1.6] text-warn">
                {reason}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Before publishing: what goes out with the document, and what cannot. */
export function DatasetsToPublish({
  datasets,
  hidden,
  onView,
}: {
  datasets: readonly PublicationDatasetDTO[];
  hidden: number;
  onView(dataset: DatasetInPlace): void;
}) {
  const going = datasets.filter((d) => d.state === "publish");
  const blocked = datasets.filter((d) => d.state !== "publish");
  if (datasets.length === 0 && hidden === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[13px] font-medium text-fg">
        {m.publishing_datasets_title()}
      </h3>
      {going.length > 0 && (
        <>
          <p className={NOTE}>{m.publishing_datasets_intro()}</p>
          <DatasetRows items={going} onView={onView} />
        </>
      )}
      {blocked.length > 0 && (
        <>
          <p className={NOTE}>
            {m.publishing_datasets_blocked({ count: blocked.length })}
          </p>
          <DatasetRows items={blocked} onView={onView} />
        </>
      )}
      {hidden > 0 && (
        <p className={NOTE}>
          {m.publishing_datasets_hidden({ count: hidden })}
        </p>
      )}
    </section>
  );
}

/** While published: the datasets out with it, and "Publish datasets" for those read but not yet. */
export function PublishedDatasets({
  pageId,
  onView,
}: {
  pageId: string;
  onView(dataset: DatasetInPlace): void;
}) {
  const api = useApi();
  const lists = useQuery(publicationDatasetsQuery(api, pageId));
  const add = useAddDatasets(pageId);
  if (!lists.data) return null;
  const { published, unpublished, hiddenCount }: PublicationDatasetsDTO =
    lists.data;
  const ready = unpublished.filter((d) => d.state === "publish");
  const publish = async () => {
    const ok = await confirmAction({
      title: m.publishing_datasets_confirm_title({ count: ready.length }),
      body: m.publishing_datasets_confirm_body(),
      action: m.publishing_datasets_publish(),
    });
    if (ok) add.mutate(ready.map((d) => d.id));
  };
  return (
    <>
      {published.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-[13px] font-medium text-fg">
            {m.publishing_datasets_published()}
          </h3>
          <DatasetRows items={published} onView={onView} />
        </section>
      )}
      {(unpublished.length > 0 || hiddenCount > 0) && (
        <section className="flex flex-col gap-2">
          <p className="text-[12.5px] leading-[1.7] text-warn">
            {m.publishing_datasets_pending()}
          </p>
          <DatasetRows items={unpublished} onView={onView} />
          {hiddenCount > 0 && (
            <p className={NOTE}>
              {m.publishing_datasets_hidden({ count: hiddenCount })}
            </p>
          )}
          {add.isError && (
            <ErrorLine>
              {apiErrorMessage(add.error, m.publishing_datasets_failed())}
            </ErrorLine>
          )}
          {ready.length > 0 && (
            <div className="flex justify-end">
              <Button disabled={add.isPending} onClick={() => void publish()}>
                {m.publishing_datasets_publish()}
              </Button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

type UiDataWant = ReturnType<typeof readDataWants>[number];

interface PreviewRequest {
  id: string;
  wants: UiDataWant[];
}

/**
 * Answers the preview frame's data requests. `going`: the datasets shown as
 * going out (any other want answers `not_found`).
 */
export function PreviewData({
  frame,
  scope,
  going,
}: {
  frame: RefObject<HTMLIFrameElement | null>;
  scope: UiDataScope;
  going: readonly string[];
}) {
  const [requests, setRequests] = useState<PreviewRequest[]>([]);
  // frontend-development: not a fetch; receives the preview frame's requests.
  useEffect(() => {
    const receive = (e: MessageEvent) => {
      const win = frame.current?.contentWindow;
      if (!win || e.source !== win) return;
      const data = e.data as Record<string, unknown> | null;
      if (
        !data ||
        data.source !== PUB_PREVIEW_SOURCE ||
        data.type !== "dataWant" ||
        typeof data.id !== "string"
      )
        return;
      const allowed = new Set(going);
      // A dataset not going out is asked as one that is not there.
      const wants = readDataWants(data.wants).map((w) =>
        allowed.has(w.dataset.toLowerCase()) ? w : { ...w, dataset: "" },
      );
      const id = data.id;
      setRequests((prev) => [...prev, { id, wants }]);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [frame, going]);
  return (
    <>
      {requests.map((r) => (
        <PreviewAnswer
          key={r.id}
          request={r}
          frame={frame}
          scope={scope}
          onDone={() =>
            setRequests((prev) => prev.filter((x) => x.id !== r.id))
          }
        />
      ))}
    </>
  );
}

function PreviewAnswer({
  request,
  frame,
  scope,
  onDone,
}: {
  request: PreviewRequest;
  frame: RefObject<HTMLIFrameElement | null>;
  scope: UiDataScope;
  onDone(): void;
}) {
  const { results } = useFenceData(request.wants, scope);
  const done = [...results.values()].every((s) => s.ready);
  // frontend-development: not a fetch (the sync is a query in useFenceData);
  // sends the results once every want has its answer.
  useEffect(() => {
    if (!done) return;
    frame.current?.contentWindow?.postMessage(
      {
        source: PUB_PREVIEW_SOURCE,
        type: "data",
        id: request.id,
        results: Object.fromEntries(results),
      },
      "*",
    );
    onDone();
  }, [done, results, frame, request.id, onDone]);
  return null;
}
